import { useCallback, useMemo } from 'react';
import { portfolioData } from '../constants/me';
import type { Experience, Project, SkillCategory, Testimonial } from '../constants/types';

// Hook for optimized data access with memoization
export const useOptimizedData = () => {
    // Memoize heavy data to prevent unnecessary re-renders
    const memoizedProjects = useMemo(() => portfolioData.projects, []);
    const memoizedSkills = useMemo(() => portfolioData.skills, []);
    const memoizedExperience = useMemo(() => portfolioData.experience, []);
    const memoizedTestimonials = useMemo(() => portfolioData.testimonials, []);

    // Lazy load project images
    const getProjectImages = useCallback(async (projectId: number): Promise<string[]> => {
        const project = memoizedProjects.find(p => p.id === projectId);
        return project?.images || [];
    }, [memoizedProjects]);

    // Get projects by category/filter
    const getFilteredProjects = useCallback((filter?: 'featured' | 'all'): Project[] => {
        if (filter === 'featured') {
            return memoizedProjects.slice(0, 4); // Show first 4 as featured
        }
        return memoizedProjects;
    }, [memoizedProjects]);

    // Get skills by category
    const getSkillsByCategory = useCallback((category: keyof SkillCategory) => {
        return memoizedSkills[category] || [];
    }, [memoizedSkills]);

    // Get recent experience
    const getRecentExperience = useCallback((limit = 3): Experience[] => {
        return memoizedExperience.slice(0, limit);
    }, [memoizedExperience]);

    // Get testimonials with pagination
    const getTestimonials = useCallback((page = 1, limit = 3): Testimonial[] => {
        const start = (page - 1) * limit;
        return memoizedTestimonials.slice(start, start + limit);
    }, [memoizedTestimonials]);

    return {
        // Data accessors
        projects: memoizedProjects,
        skills: memoizedSkills,
        experience: memoizedExperience,
        testimonials: memoizedTestimonials,
        personalInfo: portfolioData.personalInfo,
        social: portfolioData.social,
        awards: portfolioData.awards,
        adjectives: portfolioData.adjectives,

        // Utility functions
        getProjectImages,
        getFilteredProjects,
        getSkillsByCategory,
        getRecentExperience,
        getTestimonials,
    };
};

// Hook for lazy loading images
export const useLazyImages = () => {
    const loadImage = useCallback((src: string): Promise<HTMLImageElement> => {
        return new Promise((resolve, reject) => {
            const img = new Image();
            img.onload = () => resolve(img);
            img.onerror = reject;
            img.src = src;
        });
    }, []);

    const preloadImages = useCallback(async (srcs: string[]): Promise<void> => {
        try {
            await Promise.all(srcs.map(src => loadImage(src)));
        } catch (error) {
            console.warn('Failed to preload some images:', error);
        }
    }, [loadImage]);

    return {
        loadImage,
        preloadImages,
    };
}; 